import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  APP_ROUTES,
  ORDER_INBOUNDS_ROUTES,
  PAYMENT_ROUTES,
  SMART_ORDER_CONTAINER_ROUTES,
} from '@/navigators/routes';
import {
  AppModalStackParams,
  EditOrderItemNoteParams,
} from '@/navigators/types';
import {
  ChooseSubstitutionParams,
  OrderSummaryComponent,
} from '@/screens/new-order/order-summary';
import { CloseCallback } from '@/contexts/drawer-context';
import { DynamicConfetti } from './dynamic-confetti';
import { EditOrderItemNoteActionSheet } from './edit-order-item-note-action-sheet';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { OrderQueuedComponent } from '@/components/order-queued-action-sheet';
import { OrderSubmittedComponent } from '@/components/order-submitted-action-sheet';
import { OrderSubmittedComponentProps } from '@/utils/order-submitted';
import {
  PaymentMethodOption,
  usePaymentsContext,
} from '@/contexts/payments-context';
import { StyleSheet, View } from 'react-native';
import { SubstitutionChoiceParams } from '@/components/substitutions/types';
import {
  SubstitutionsActionSheet,
  Props as SubstitutionSheetProps,
} from '@/components/substitutions/substitutions-action-sheet';
import { TabView, Props as TabViewProps } from '@/components/tab-view';
import { useGetSubstitutionOptions } from '@/components/substitutions/hooks/use-get-substitution-options';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOfflineOrderContext } from '@/hooks/use-offline-data';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';
import { useSmartOrderParams } from '@/hooks/use-smart-order-params';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { WalletActionSheet } from '@/components/payments/wallet-action-sheet';

type SubstitutionStateType = Omit<SubstitutionSheetProps, 'onClose'>;

export enum OrderDrawerViews {
  Placed = 'PLACED',
  Queued = 'QUEUED',
  Summary = 'SUMMARY',
}

const OrderDrawerScreenIndices = {
  [OrderDrawerViews.Placed]: 0,
  [OrderDrawerViews.Summary]: 1,
  [OrderDrawerViews.Queued]: 2,
};

interface Props {
  closeDrawer: () => void;
  onSubmitOrder?: () => void;
  onSubmitSmartOrder?: (smartOrderUUID: string) => void;
  setCloseable: (closeable: boolean) => void;
  setCloseCallback: (callback: CloseCallback) => void;
  smartOrderUUID?: string;
}

interface TabRoute {
  key: OrderDrawerViews;
}

export const OrderDrawerManager: React.FC<Props> = ({
  closeDrawer,
  onSubmitOrder,
  setCloseable,
  setCloseCallback,
  smartOrderUUID,
  onSubmitSmartOrder,
}) => {
  const navigation = useNavigation<NavigationProp<AppModalStackParams>>();
  const navigate = navigation.navigate;
  const { showErrorToast, showToast } = useToasts();
  const getSubstitutionOptions = useGetSubstitutionOptions();
  const { loading, selectedPaymentMethod, setPaymentContextState } =
    usePaymentsContext();
  const [showWallet, setShowWallet] = useState<boolean>(false);
  const navigateToPaymentMethod = () => setShowWallet(true);
  const onSelectPaymentMethod = (paymentMethod?: PaymentMethodOption) => {
    setPaymentContextState({ selectedPaymentMethod: paymentMethod });
    setShowWallet(false);
  };
  const isOfflineModeActive = useOfflineModeActive();
  const smartOrderParams = useSmartOrderParams();
  const addRemovedOrderUUID = useOrderInboundsStore(
    (store) => store.actions.addRemovedOrderUUID,
  );

  const [submittedOrderProps, setSubmittedOrderProps] =
    useState<OrderSubmittedComponentProps>({
      externalReviewRequired: false,
      fulfillmentDates: [],
      isShippingOrder: false,
      isSplitOrder: false,
      loyaltyEarns: [],
      reviewRequired: false,
      unpaidInvoice: undefined,
    });
  const [substitutionSheetProps, setSubstitutionSheetProps] =
    useState<SubstitutionStateType | null>(null);
  const [editOrderItemNoteSheetProps, setEditOrderItemNoteSheetProps] =
    useState<EditOrderItemNoteParams | null>(null);
  const [isFocused, setIsFocused] = useState(true);
  const { completionToast } = submittedOrderProps;
  const [optimisticSubstitution, setOptimisticSubstitution] =
    useState<SubstitutionChoiceParams>();
  const { draftOrder, restaurantUUID, supplierUUID } = useNewOrderContext();
  const { offlineOrderContext } = useOfflineOrderContext(
    restaurantUUID!,
    supplierUUID!,
  );

  useEffect(() => {
    if (completionToast) {
      setCloseCallback(() => () => showToast(completionToast));
    }
  }, [setCloseCallback, showToast, completionToast]);

  const closeDrawerCallback = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const [tabIndex, setTabIndex] = useState(
    OrderDrawerScreenIndices[OrderDrawerViews.Summary],
  );

  const navigationState = useMemo(() => {
    return {
      index: tabIndex,
      routes: [
        {
          key: OrderDrawerViews.Placed,
        },
        {
          key: OrderDrawerViews.Summary,
        },
        {
          key: OrderDrawerViews.Queued,
        },
      ],
    };
  }, [tabIndex]);

  const {
    selectedRestaurantRelationship: {
      appPaymentsEnabled,
      employeeRole,
      showPaymentsTab,
    },
  } = useUserContext();

  const clearSubChoice = useCallback(() => {
    setOptimisticSubstitution(undefined);
  }, []);

  const renderScene: TabViewProps<TabRoute>['renderScene'] = useCallback(
    ({ route, jumpTo }) => {
      switch (route.key) {
        case OrderDrawerViews.Summary:
          return (
            <>
              <View style={styles.titlePadding}>
                <OrderSummaryComponent
                  chooseSubstitution={async ({
                    itemWithSubstitute,
                    fulfillmentDate,
                    orderUUID,
                    variantPacksInOrder,
                  }: ChooseSubstitutionParams) => {
                    const variantPackUUID =
                      itemWithSubstitute.substitution!.original_variant_pack
                        .uuid;
                    try {
                      const { availabilityMap, substitutionListOptions } =
                        await getSubstitutionOptions({
                          fulfillmentDate,
                          variantPacksInOrder,
                          variantPackUUID,
                        });

                      setSubstitutionSheetProps({
                        availabilityMap,
                        fulfillmentDate,
                        itemWithSubstitute,
                        orderUUID,
                        substitutionListOptions,
                        variantPacksInOrder,
                      });
                      setIsFocused(false);
                    } catch (e) {
                      showErrorToast();
                    }
                  }}
                  clearInitialSubstitutionChoice={clearSubChoice}
                  editOrderItemNote={setEditOrderItemNoteSheetProps}
                  hasFocus={isFocused}
                  initialSubstitutionChoice={optimisticSubstitution}
                  loadingPaymentMethod={loading}
                  navigateBack={closeDrawerCallback}
                  navigateToPaymentMethod={navigateToPaymentMethod}
                  navigationHeaderProps={{
                    showShadow: false,
                  }}
                  onOrderPlaced={(props) => {
                    const submittedSmartOrderUUID =
                      smartOrderUUID || smartOrderParams?.smartOrderUUID;
                    if (submittedSmartOrderUUID) {
                      addRemovedOrderUUID(submittedSmartOrderUUID);
                    }

                    onSubmitOrder?.();
                    if (props === null) {
                      const draftOrderUUID = draftOrder?.uuid;
                      const matchingOrder =
                        offlineOrderContext.data?.getOrders.find(
                          (o) => o.order?.uuid === draftOrderUUID,
                        );
                      if (isOfflineModeActive && matchingOrder) {
                        jumpTo(OrderDrawerViews.Queued);
                      }
                      return;
                    }
                    if (props.externalReviewRequired) {
                      setCloseable(false);
                    }

                    setSubmittedOrderProps({
                      ...props,
                      canPayInvoice: Boolean(
                        props.unpaidInvoice &&
                          showPaymentsTab &&
                          appPaymentsEnabled &&
                          employeeRole,
                      ),
                    });
                    setIsFocused(false);

                    if (smartOrderParams && draftOrder?.uuid) {
                      closeDrawerCallback();
                      navigate(SMART_ORDER_CONTAINER_ROUTES.PlacedOrder, {
                        orderUUID: draftOrder.uuid,
                        submittedOrderProps: props,
                      });
                    } else if (submittedSmartOrderUUID) {
                      navigate(ORDER_INBOUNDS_ROUTES.List);
                      jumpTo(OrderDrawerViews.Placed);
                      onSubmitSmartOrder?.(submittedSmartOrderUUID);
                    } else {
                      jumpTo(OrderDrawerViews.Placed);
                    }
                  }}
                  selectedPaymentMethod={selectedPaymentMethod}
                  setCloseable={setCloseable}
                  skipListInteractionCheck
                  smartOrderUUID={
                    smartOrderUUID || smartOrderParams?.smartOrderUUID || null
                  }
                />
              </View>
              {substitutionSheetProps ? (
                <SubstitutionsActionSheet
                  {...substitutionSheetProps}
                  onClose={(payload) => {
                    if (payload) {
                      setOptimisticSubstitution(payload);
                    }
                    setSubstitutionSheetProps(null);
                    setIsFocused(true);
                  }}
                />
              ) : null}
              {editOrderItemNoteSheetProps ? (
                <EditOrderItemNoteActionSheet
                  {...editOrderItemNoteSheetProps}
                  onClose={() => setEditOrderItemNoteSheetProps(null)}
                />
              ) : null}
            </>
          );
        case OrderDrawerViews.Placed:
          return (
            <View style={styles.centered} testID="order-placed-component">
              <DynamicConfetti
                isCelebrating={
                  tabIndex ===
                    OrderDrawerScreenIndices[OrderDrawerViews.Placed] &&
                  !submittedOrderProps.externalReviewRequired &&
                  !submittedOrderProps.reviewRequired
                }
              />
              <OrderSubmittedComponent
                {...submittedOrderProps}
                onPayNowPress={() => {
                  navigate(APP_ROUTES.Payments, {
                    params: {
                      unpaidInvoice: submittedOrderProps.unpaidInvoice,
                    },
                    screen: PAYMENT_ROUTES.Base,
                  });
                  closeDrawerCallback();
                }}
              />
            </View>
          );
        case OrderDrawerViews.Queued:
          return (
            <View style={styles.centered} testID="order-queued-component">
              <DynamicConfetti
                isCelebrating={
                  tabIndex === OrderDrawerScreenIndices[OrderDrawerViews.Queued]
                }
              />
              <OrderQueuedComponent />
            </View>
          );
        default:
          return null;
      }
    },
    [
      addRemovedOrderUUID,
      appPaymentsEnabled,
      clearSubChoice,
      closeDrawerCallback,
      draftOrder?.uuid,
      employeeRole,
      getSubstitutionOptions,
      isFocused,
      isOfflineModeActive,
      loading,
      navigate,
      offlineOrderContext.data?.getOrders,
      onSubmitOrder,
      onSubmitSmartOrder,
      optimisticSubstitution,
      selectedPaymentMethod,
      setCloseable,
      showErrorToast,
      editOrderItemNoteSheetProps,
      showPaymentsTab,
      smartOrderParams,
      smartOrderUUID,
      submittedOrderProps,
      substitutionSheetProps,
      tabIndex,
    ],
  );

  return (
    <>
      <TabView<TabRoute>
        label={() => null}
        navigationState={navigationState}
        onIndexChange={setTabIndex}
        renderScene={renderScene}
        renderTabBar={() => null}
      />
      {showWallet ? (
        <WalletActionSheet
          onClose={() => {
            setShowWallet(false);
          }}
          onSavePaymentMethod={() => {
            setShowWallet(false);
          }}
          onSelectPaymentMethod={onSelectPaymentMethod}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    justifyContent: 'center',
    marginHorizontal: 42,
  },
  titlePadding: {
    flex: 1,
    paddingTop: 8,
  },
});
